import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UserService } from 'src/app/services/user.service';
import { SettingsService } from 'src/app/services/settings.service';
import { FiaReservation } from 'src/app/shared/models/users/fia-reservation';
import { AddFiaReservationRequest } from 'src/app/shared/models/users/add-fia-reservation-request';
import { UserProfile } from 'src/app/shared/models/users/user-profile';
import { FiaService } from 'src/app/services/fia.service';
import { FiaLeftToApplyFor } from 'src/app/shared/models/users/fia-left-to-apply-for';

@Component({
  selector: 'app-fia-reserved',
  templateUrl: './fia-reserved.component.html',
  styleUrls: ['./fia-reserved.component.scss'],
})
export class FiaReservedComponent implements OnInit, OnChanges {
  @Input() selectedUserProfile: UserProfile;

  loading: boolean = true;
  loadingAvailable: boolean = false;
  errorResponse: HttpErrorResponse;
  showError: boolean = false;

  fiaAvailable: number = 0;
  fiaAvailableError: string = '';
  currentFiaReservedAmount: number = 0;
  reservations: FiaReservation[] = [];
  reservationsByYear: { [year: number]: FiaReservation[] } = {};

  constructor(
    private userService: UserService,
    public settingsService: SettingsService,
    public fiaService: FiaService
  ) {}

  ngOnInit(): void {
    if (this.selectedUserProfile.userId) {
      this.loadReservations();
      this.loadSdaAndFiaAvailable();
      this.currentFiaReservedAmount = this.selectedUserProfile.adminFiaAmount ?? 0;
    } else {
      this.loading = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedUserProfile'] && !changes['selectedUserProfile'].firstChange && this.selectedUserProfile.userId) {
      this.loadReservations();
      this.loadSdaAndFiaAvailable();
    }
  }

  loadSdaAndFiaAvailable(): void {
    this.loadingAvailable = true;
    this.fiaService.CalculateLeftToApplyFor(this.selectedUserProfile.userId).subscribe(
      (data: FiaLeftToApplyFor) => {
        this.fiaAvailable = data?.fiaAvailable ?? 0;
        this.fiaAvailableError = '';
        this.loadingAvailable = false;
      },
      (error) => {
        console.error(error);
        this.fiaAvailableError = 'Something went wrong while loading FIA available.';
        this.loadingAvailable = false;
      }
    );
  }

  loadReservations(): void {
    this.loading = true;
    this.userService.GetFiaReservations(this.selectedUserProfile.userId).subscribe(
      (data: FiaReservation[]) => {
        this.reservations = data;
        this.organizeReservationsByYear();
        this.currentFiaReservedAmount = this.calculateTotalReservedForYear(this.currentYear);
        this.loading = false;
      },
      (error) => {
        this.parseAndDisplayError(error);
        this.loading = false;
      }
    );
  }

  organizeReservationsByYear(): void {
    this.reservationsByYear = {};
    this.availableYears.forEach((year) => {
      this.reservationsByYear[year] = this.reservations.filter((r) => r.year === year);
    });
  }

  calculateTotalReservedForYear(year: number): number {
    return this.reservationsByYear[year].reduce((total, reservation) => total + reservation.amount, 0);
  }

  hasDifference(): boolean {
    return this.calculateTotalReservedForYear(this.currentYear) !== this.currentFiaReservedAmount;
  }

  parseAndDisplayError(error: any) {
    this.errorResponse = error;
    this.showError = true;
  }

  currentYear = new Date().getFullYear();
  availableYears = [this.currentYear, this.currentYear + 1];

  newReservationYear: number | null = null;
  newReservationAmount: number | null = null;
  newReservationComment: string = '';

  get isFormValid(): boolean {
    return this.newReservationYear !== null && this.newReservationAmount !== null && this.newReservationComment.trim() !== '';
  }

  addReservation() {
    if (!this.isFormValid) {
      return;
    }

    const request: AddFiaReservationRequest = {
      userId: this.selectedUserProfile.userId,
      amount: this.newReservationAmount,
      year: this.newReservationYear,
      comment: this.newReservationComment,
      actionByUserId: this.settingsService.userProfile.userId,
    };

    this.loading = true;
    this.showError = false;

    this.userService.AddFiaReservation(request).subscribe(
      () => {
        // Clear form after successful add
        this.newReservationYear = null;
        this.newReservationAmount = null;
        this.newReservationComment = '';

        // Reload reservations to show the new one
        this.loadReservations();
        this.loadSdaAndFiaAvailable();
      },
      (error) => {
        this.parseAndDisplayError(error);
        this.loading = false;
      }
    );
  }

  getReservationsForYear(year: number): FiaReservation[] {
    return this.reservationsByYear[year] || [];
  }
}
